// src/components/CustomerTracking/CustomerTracking.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RiSearchLine, RiLoaderLine, RiCheckLine, RiTimeLine, RiCloseLine, RiTruckLine, RiCalendarLine } from 'react-icons/ri';
import './CustomerTracking.css';
import { BASE_URL } from '../utils/constants';


const CustomerTracking = () => {
  const [reference, setReference] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '0.00';
    const amount = Number(value);
    return Number.isNaN(amount) ? String(value) : amount.toFixed(2);
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  };

  const formatLabel = (value) => {
    if (!value) return 'Unknown';
    const text = String(value).trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const buildPaymentInfo = () => {
    if (!trackingData) return {};
    return (
      trackingData.payment ||
      (Array.isArray(trackingData.payments) && trackingData.payments[0]) ||
      trackingData.paymentInfo ||
      {}
    );
  };

  const getOrderItems = () => {
    return (
      trackingData?.items ||
      trackingData?.orderItems ||
      trackingData?.products ||
      []
    );
  };

  const getShippingAddress = () => {
    return (
      trackingData?.deliveryAddress ||
      trackingData?.shippingAddress ||
      trackingData?.shipping ||
      {}
    );
  };

  const checkPaymentStatus = async () => {
    const trimmedReference = reference.trim();
    if (!trimmedReference) {
      setError('Please enter your order reference');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BASE_URL}/collections/status/${trimmedReference}`);
      const data = await response.json();
      const paymentStatus = data?.data?.status || data?.status || '';
      if (!response.ok) {
        throw new Error(data?.message || 'Could not fetch payment status');
      }

      setTrackingData((prev) => ({
        ...prev,
        paymentStatus: paymentStatus || prev?.paymentStatus,
        paymentInfo: {
          ...buildPaymentInfo(),
          ...data?.data
        }
      }));
    } catch (err) {
      console.error('Payment status error:', err);
      setError(err.message || 'Unable to check payment status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTracking = async (referenceValue) => {
    if (!referenceValue) return;

    setLoading(true);
    setError(null);
    setTrackingData(null);

    try {
      const response = await fetch(`${BASE_URL}/api/orders/${referenceValue}/tracking`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Order not found. Please check the reference number.');
        }
        throw new Error('Failed to fetch tracking information');
      }

      const data = await response.json();
      setTrackingData(data);
    } catch (err) {
      console.error('Tracking error:', err);
      setError(err.message || 'Unable to load tracking details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmedReference = reference.trim();
    if (!trimmedReference) {
      setError('Please enter your order reference');
      return;
    }

    await fetchTracking(trimmedReference);
  };

    useEffect(() => {
      const autoRef = (searchParams.get('ref') || searchParams.get('reference') || '').trim();
      if (autoRef) {
        setReference(autoRef);
        fetchTracking(autoRef);
      }
    }, [searchParams]);

    const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'successful':
      case 'paid':
      case 'delivered':
        return <RiCheckLine className="status-icon success" />;
      case 'failed':
      case 'payment_failed':
        return <RiCloseLine className="status-icon failed" />;
      case 'shipped':
      case 'in transit':
      case 'out for delivery':
        return <RiTruckLine className="status-icon transit" />;
      default:
        return <RiTimeLine className="status-icon pending" />;
    }
  };

  const getStatusText = (paymentStatus, deliveryStatus) => {
    if ((paymentStatus || '').toLowerCase() === 'failed') return 'Payment Failed';
    if ((deliveryStatus || '').toLowerCase() === 'delivered') return 'Delivered';
    if ((deliveryStatus || '').toLowerCase() === 'shipped') return 'Shipped';
    if ((deliveryStatus || '').toLowerCase() === 'processing') return 'Processing';
    if ((deliveryStatus || '').toLowerCase() === 'pending') return 'Pending';
    return 'In Progress';
  };

  return (
    <div className="tracking-page">
      <header className="tracking-header">
        <h1>Track Your Order</h1>
        <p>Enter your order reference number to view real-time status</p>
      </header>

      <main className="tracking-main">
        <form onSubmit={handleSearch} className="search-form">
          <div className="input-group">
            <RiSearchLine className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Order Reference (e.g. ref_123456789)"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={loading}
            />
          </div>
          <button type="submit" disabled={loading || !reference.trim()} className="track-btn">
            {loading ? (
              <>
                <RiLoaderLine className="spin" /> Tracking...
              </>
            ) : (
              'Track Order'
            )}
          </button>
        </form>

        {error && <div className="error-message">{error}</div>}

        {trackingData && (
          <div className="tracking-result">
            <div className="order-summary">
              <h2>Order #{trackingData.reference}</h2>

              <div className="status-section">
                <div className="status-badge">
                  {getStatusIcon(trackingData.paymentStatus)}
                <span>Payment: {formatLabel(trackingData.paymentStatus)}</span>
                </div>

                <div className="status-badge">
                  {getStatusIcon(trackingData.deliveryStatus)}
                  <span>Delivery: {getStatusText(trackingData.paymentStatus, trackingData.deliveryStatus)}</span>
                </div>
              </div>

              {trackingData.estimatedDeliveryDate && (
                <div className="estimated-delivery">
                  <RiCalendarLine size={20} />
                  <span>Estimated Delivery: {formatDate(trackingData.estimatedDeliveryDate)}</span>
                </div>
              )}

              <div className="order-meta">
                <p><strong>Total Items:</strong> {trackingData.itemsCount ?? getOrderItems().length}</p>
                <p><strong>Total Amount:</strong> ZMK {formatAmount(trackingData.totalAmount)}</p>
              </div>
            </div>
 
            <section className="section-card">
              <h3 className="section-title">Payment Details</h3>
              <div className="details-grid">
                <div><strong>Status</strong><p>{formatLabel(trackingData.paymentStatus)}</p></div>
                <div><strong>Method</strong><p>{formatLabel(buildPaymentInfo().method || buildPaymentInfo().paymentMethod || buildPaymentInfo().payment_type || 'Unknown')}</p></div>
                <div><strong>Transaction</strong><p>{buildPaymentInfo().transactionId || buildPaymentInfo().txId || buildPaymentInfo().reference || 'N/A'}</p></div>
                <div><strong>Amount</strong><p>ZMK {formatAmount(buildPaymentInfo().amount || trackingData.totalAmount)}</p></div>
                <div><strong>Paid On</strong><p>{formatDate(buildPaymentInfo().date || buildPaymentInfo().createdAt || trackingData.createdAt)}</p></div>
              </div>
              <button className="check-payment-btn" onClick={checkPaymentStatus} disabled={loading}>
                {loading ? <><RiLoaderLine className="spin" /> Checking Payment...</> : 'Check Payment Status'}
              </button>
            </section>
 
            <section className="section-card">
              <h3 className="section-title">Items Ordered</h3>
              {getOrderItems().length > 0 ? (
                getOrderItems().map((item, index) => (
                  <div key={index} className="item-row">
                    <div>
                      <h4>{item.title || item.name || item.productName || `Item ${index + 1}`}</h4>
                      <p className="item-description">{item.description || item.details || item.summary || 'No description available.'}</p>
                      <p className="item-meta">Qty: {item.quantity ?? item.qty ?? 1}</p>
                    </div>
                    <div className="item-pricing">
                      <p>{`ZMK ${formatAmount(item.price || item.unit_price || item.amount || 0)}`}</p>
                      <p>{item.quantity ? `Total: ZMK ${formatAmount((item.price || item.unit_price || item.amount || 0) * item.quantity)}` : null}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p>No item details available for this order.</p>
              )}
            </section>
 
            {Object.keys(getShippingAddress()).length > 0 && (
              <section className="section-card">
                <h3 className="section-title">Delivery Address</h3>
                <div className="details-grid">
                  <div><strong>Name</strong><p>{getShippingAddress().fullName || getShippingAddress().name || getShippingAddress().recipient || 'N/A'}</p></div>
                  <div><strong>Address</strong><p>{[getShippingAddress().street, getShippingAddress().city, getShippingAddress().province, getShippingAddress().postalCode].filter(Boolean).join(', ') || 'N/A'}</p></div>
                  <div><strong>Phone</strong><p>{getShippingAddress().phone || trackingData.customer?.phone || 'N/A'}</p></div>
                </div>
              </section>
            )}
 
            <div className="tracking-info">
              <h3>Order Status Timeline</h3>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <h4>Order Placed</h4>
                    <p>Reference created and payment initiated</p>
                  </div>
                </div>

                {trackingData.paymentStatus?.toLowerCase() === 'successful' && (
                  <div className="timeline-item active">
                    <div className="timeline-dot success" />
                    <div className="timeline-content">
                      <h4>Payment Confirmed</h4>
                      <p>Funds received successfully</p>
                    </div>
                  </div>
                )}

                {trackingData.deliveryStatus?.toLowerCase() !== 'pending' && trackingData.deliveryStatus ? (
                  <div className="timeline-item active">
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <h4>Processing Started</h4>
                      <p>Order is being prepared</p>
                    </div>
                  </div>
                ) : null}

                {trackingData.deliveryStatus?.toLowerCase() === 'shipped' && (
                  <div className="timeline-item active">
                    <div className="timeline-dot transit" />
                    <div className="timeline-content">
                      <h4>Shipped</h4>
                      <p>Parcel is on its way</p>
                    </div>
                  </div>
                )}

                {trackingData.deliveryStatus?.toLowerCase() === 'delivered' && (
                  <div className="timeline-item active">
                    <div className="timeline-dot success" />
                    <div className="timeline-content">
                      <h4>Delivered</h4>
                      <p>Package successfully delivered to customer</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button className="new-search-btn" onClick={() => {
              setReference('');
              setTrackingData(null);
              setError(null);
            }}>
              Track Another Order
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default CustomerTracking;
// import React from 'react';
// import { RiArrowLeftLine, RiMapPinLine, RiTruckLine, RiArchiveLine as RiPackageLine, RiPhoneLine, RiErrorWarningLine as RiAlertTriangleLine } from 'react-icons/ri';
// import './CustomerTracking.css';

// const CustomerTracking = () => {
//   const timelineSteps = [
//     {
//       status: 'Order Placed',
//       time: 'Dec 15, 2024 at 10:30 AM',
//       completed: true,
//     },
//     {
//       status: 'Picked Up',
//       time: 'Dec 16, 2024 at 9:00 AM',
//       completed: true,
//     },
//     {
//       status: 'In Transit',
//       time: 'Today, 1:30 PM • Distribution Center',
//       completed: true,
//     },
//     {
//       status: 'Out for Delivery',
//       time: 'Today, 2:15 PM • Midtown Hub',
//       completed: false,
//       current: true,
//     },
//     {
//       status: 'Delivered',
//       time: 'Estimated: Today, 4:00–6:00 PM',
//       completed: false,
//     },
//   ];

//   return (
//     <div className="customer-tracking-page">
//       <header className="mobile-header">
//         <button className="back-btn">
//           <RiArrowLeftLine size={24} />
//         </button>
//         <h1>Track Your Order</h1>
//       </header>

//       <main className="tracking-content">
//         <section className="order-summary">
//           <div className="order-header">
//             <h2>Order #ORD-2024-001842</h2>
//             <span className="status-badge current">Out for Delivery</span>
//           </div>
//           <p className="estimated-time">Estimated delivery: Today, 4:00–6:00 PM</p>
//         </section>

//         <section className="timeline-section">
//           <div className="timeline-container">
//             {timelineSteps.map((step, index) => (
//               <div
//                 key={index}
//                 className={`timeline-item ${step.completed ? 'completed' : ''} ${
//                   step.current ? 'current' : ''
//                 }`}
//               >
//                 <div className="timeline-dot">
//                   {step.current ? (
//                     <RiTruckLine size={20} />
//                   ) : step.completed ? (
//                     <RiPackageLine size={20} />
//                   ) : (
//                     <div className="empty-dot" />
//                   )}
//                 </div>
//                 <div className="timeline-content">
//                   <h3 className="step-title">{step.status}</h3>
//                   <p className="step-time">{step.time}</p>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         <section className="items-section">
//           <h3>Items in this order</h3>
//           <div className="items-list">
//             <div className="item-row">
//               <div className="item-info">
//                 <p className="item-name">Wireless Earbuds Pro</p>
//                 <p className="item-qty">Quantity: 1</p>
//               </div>
//               <p className="item-price">$89.99</p>
//             </div>
//             <div className="item-row">
//               <div className="item-info">
//                 <p className="item-name">USB-C Charging Cable</p>
//                 <p className="item-qty">Quantity: 1</p>
//               </div>
//               <p className="item-price">$12.52</p>
//             </div>
//           </div>
//         </section>

//         <section className="address-section">
//           <h3>Delivery Address</h3>
//           <div className="address-card">
//             <p className="name">John Smith</p>
//             <p>123 Main Street, Apt 4B</p>
//             <p>New York, NY 10001</p>
//             <div className="instructions">
//               <strong>Special Instructions:</strong>
//               <p>Leave package with building concierge</p>
//             </div>
//           </div>
//         </section>

//         <section className="help-section">
//           <h3>Need Help?</h3>
//           <div className="help-buttons">
//             <button className="help-btn primary">
//               <RiPhoneLine /> Contact Support
//             </button>
//             <button className="help-btn">
//               <RiAlertTriangleLine /> Report delivery issue
//             </button>
//             <button className="help-btn">
//               Request delivery reschedule
//             </button>
//           </div>
//         </section>
//       </main>
//     </div>
//   );
// };

// export default CustomerTracking;